import { VisibilityElement } from '@/components/custom-elements/visibility'

/**
 * MODAL CUSTOM ELEMENT
 * Handled as a custom element due to their being next to no state to manage, and due to how neatly it interacts with Tailwind
 *
 * For set up:
 *   1. Ensure you wrap your modal elements with <x-modal>.
 *   2. Pass a 'data-id' if you intend to nest modals.
 *   3. Provide any buttons you want to modify modal state as a child, using "data-modal-role" with open, close, or toggle.
 *   4. To show or hide elements based on modal state, set a tailwind group and use "group-data-visible:" for stateful styling.
 *   5. Optional: Add 'data-hide-on-blur="true"' to hide the modal when it loses focus.
 *
 */

export class ModalElement extends VisibilityElement {
  // Child role selectors
  static OPEN_SELECTOR = '[data-modal-role~="open"]'
  static CLOSE_SELECTOR = '[data-modal-role~="close"]'
  static TOGGLE_SELECTOR = '[data-modal-role~="toggle"]'
  static DISABLE_SELECTOR = '[data-modal-role~="disable"]'

  openElement: NodeListOf<Element> | undefined
  closeElement: NodeListOf<Element> | undefined
  toggleElement: NodeListOf<Element> | undefined
  disableElement: NodeListOf<Element> | undefined
  hideOnBlur: boolean = false

  static openModalById(elementId: string) {
    const targetElement = document.getElementById(elementId)

    // Check if the element is a modal (accordion)
    if (targetElement && targetElement.tagName === 'X-MODAL') {
      const modalElement = targetElement as ModalElement
      // Open the accordion
      modalElement.handleShow()
      // Scroll to it smoothly after a short delay to allow for opening animation
      setTimeout(() => {
        const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset
        const offsetPosition = elementPosition - 100

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      }, 100)
    }
  }

  static handleHashChange() {
    const hash = window.location.hash
    if (!hash) return
    const elementId = hash.substring(1)
    ModalElement.openModalById(elementId)
  }

  static handleAnchorClick(event: MouseEvent) {
    const target = event.currentTarget as HTMLAnchorElement
    const href = target.getAttribute('href')

    if (!href || !href.startsWith('#')) return

    const elementId = href.substring(1)
    ModalElement.openModalById(elementId)
  }

  parseIdSelector() {
    const id = this.dataset.id
    return id ? `[data-modal-parent~="${id}"]` : ''
  }

  fetchChildren() {
    const idSelector = this.parseIdSelector()
    const targetWrapper = idSelector ? document : this
    this.openElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.OPEN_SELECTOR}`)
    this.closeElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.CLOSE_SELECTOR}`)
    this.toggleElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.TOGGLE_SELECTOR}`)
    this.disableElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.DISABLE_SELECTOR}`)
  }

  handleBlur = (event: FocusEvent) => {
    // Only run if hideOnBlur is enabled
    if (!this.hideOnBlur) return

    // Check if the related target (that received focus) is inside this element
    // If not, hide the modal
    const currentTarget = event.currentTarget as Node
    const relatedTarget = event.relatedTarget as Node

    if (relatedTarget && !currentTarget.contains(relatedTarget)) {
      this.handleHide()
    }
  }

  parseConfig() {
    // Parse hideOnBlur from the data attribute
    this.hideOnBlur = this.dataset.hideOnBlur === 'true'
  }

  addEventListeners() {
    super.addEventListeners()
    this.openElement?.forEach((el) => el?.addEventListener('click', this.handleShow))
    this.closeElement?.forEach((el) => el?.addEventListener('click', this.handleHide))
    this.toggleElement?.forEach((el) => el?.addEventListener('click', this.handleToggle))
    this.disableElement?.forEach((el) => el?.addEventListener('click', this.handleDisable))

    // Add blur event listener if hideOnBlur is enabled
    if (this.hideOnBlur) {
      this.addEventListener('focusout', this.handleBlur)
    }
  }

  removeEventListeners() {
    super.removeEventListeners()
    this.openElement?.forEach((el) => el?.removeEventListener('click', this.handleShow))
    this.closeElement?.forEach((el) => el?.removeEventListener('click', this.handleHide))
    this.toggleElement?.forEach((el) => el?.removeEventListener('click', this.handleToggle))
    this.disableElement?.forEach((el) => el?.removeEventListener('click', this.handleDisable))

    // Remove blur event listener if it was added
    if (this.hideOnBlur) {
      this.removeEventListener('focusout', this.handleBlur)
    }
  }

  // Expose the event handlers for external buttons
  getListeners() {
    return {
      open: this.handleShow,
      close: this.handleHide,
      toggle: this.handleToggle,
      disable: this.handleDisable
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.parseConfig()
    this.fetchChildren()
    this.addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }
}

// Handle hash-based accordion opening on page load and hash changes
if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', () => {
    ModalElement.handleHashChange()
    const anchorLinks = document.querySelectorAll('a[href^="#"]')
    anchorLinks.forEach((link) => {
      link.addEventListener('click', ModalElement.handleAnchorClick)
    })
  })

  // Handle hash changes (e.g., when clicking anchor links)
  window.addEventListener('hashchange', () => {
    ModalElement.handleHashChange()
  })
}
